import { Http, Headers } from '@angular/http';
import { Injectable } from '@angular/core';
import { Session } from 'app/shared/services/session/session.service';
import { CONFIG } from 'app/config/config';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActiveUser } from 'app/shared/services/user/active-user.model';
import { Toast } from 'app/shared/components/toast/toast.service';
import { UserService } from 'app/shared/services/user/user.service';
import { ContentValidators } from 'app/validators/content/content-validators';
import { ApplicationService } from 'app/shared/services/app/application/application.service';

@Injectable()
export class DashboardService {
  user: ActiveUser;
  whatsNewForm: FormGroup;
  articleToEdit: any;
  editMode: boolean = false;
  articles: Array<{ article: { title: string, description: string, descriptionHTML: string, excerptHTML: string, created_at: string }, read: boolean }> = [];
  anyUnreadArticles: boolean = false;

  tutorialForm: FormGroup;
  tutorialToEdit: any;
  tutorialToDelete: any;
  tutorials: any[] = [];

  hasFeaturesToPublish: boolean = false; // switches state of the card if there are no features to publish
  versions: any[] = [];
  modifiedFeatures: any[] = [];

  statusOptions: any = {
    'not-started': { displayName: 'Not Started', color: 'empty' },
    'blocked': { displayName: 'Blocked', color: 'red' },
    'initial-setup': { displayName: 'Initial Setup', color: 'yellow' },
    'needs-content': { displayName: 'Needs Content', color: 'yellow' },
    'testing': { displayName: 'Testing', color: 'yellow' },
    'app-store-review': { displayName: 'App Store Review', color: 'yellow' },
    'in-production': { displayName: 'In Production', color: 'green' },
  };

  constructor(private http: Http,
              private applicationService: ApplicationService,
              private formBuilder: FormBuilder,
              private toast: Toast,
              private session: Session,
              private userService: UserService) {
    this.whatsNewForm = formBuilder.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required]]
    });
    this.tutorialForm = formBuilder.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required]],
      link: ['', [Validators.required, ContentValidators.mustBeUrl]]
    });
  }

  refreshDashboard() {
    this.session.user()
      .then((user) => {
        this.user = user;
        return this.session.refreshDashboard();
      })
      .then(() => {
        this.createArticleArray(this.session.dashboard.whats_new.reverse());
        this.tutorials = this.session.dashboard.help.reverse();
        if (this.session.selectedAppSysName.length > 0) return this.refreshVersionHistory();
      })
      .catch((e) => {
        console.log(e);
      });
  }

  refreshVersionHistory() {
    return this.applicationService.getModifiedFeatures(this.session.selectedAppSysName)
    .then((response) => {
      this.modifiedFeatures = response;
      this.hasFeaturesToPublish = response.length > 0;
      return this.applicationService.getVersionHistory(this.session.selectedAppSysName)
    })
    .then((response) => {
      this.versions = response.reverse();
    });
  }

  getFeaturesListString(version: any) {
    return version.updated_features
      .map(feature => this.applicationService.getDisplayNameForFeature(feature))
      .join(', ');
  }

  createArticleArray(articles: any[]) {
    let cutOffDate = new Date(new Date().setDate(new Date().getDate() - 30)); // 30 days in the past
    this.articles = [];
    articles.forEach((article) => {
      let readState = this.user.readArticleIds.indexOf(article.id) != -1;
      // even if it's unread, we don't care once it's older than 30 days (this does not work properly when combined with the line above)
      if (!readState) readState = new Date(article.created_at) < cutOffDate;

      this.articles.push({ article: article, read: readState });

      if (!readState) this.anyUnreadArticles = true; // if one is false, there is an unread one
    });
  }

  // to update 'anyUnreadArticles' after initialization
  checkForAnyUnreadArticles(articleMap: any[]) {
    let hasAnyUnread = false;
    for (let i = 0; i < articleMap.length; i++) {
      if (!articleMap[i].read) {
        hasAnyUnread = true;
        break;
      }
    }
    this.anyUnreadArticles = hasAnyUnread;
  }

  editNewsItem(article: any) {
    this.editMode = true;
    this.articleToEdit = article;
    this.whatsNewForm.controls['title'].setValue(article.title);
    this.whatsNewForm.controls['description'].setValue(article.description);
  }

  editTutorial(tutorial: any) {
    this.tutorialToEdit = tutorial;
    this.editMode = true;
    this.tutorialForm.controls['title'].setValue(tutorial.title);
    this.tutorialForm.controls['description'].setValue(tutorial.description);
    this.tutorialForm.controls['link'].setValue(tutorial.link);
  }

  resetForm() {
    this.editMode = false;

    this.whatsNewForm.controls['title'].setValue('');
    this.whatsNewForm.controls['description'].setValue('');

    this.tutorialForm.controls['title'].setValue('');
    this.tutorialForm.controls['description'].setValue('');
    this.tutorialForm.controls['link'].setValue('');
  }

  confirmSaveProductChange() {
    if (!this.editMode) {
      return this.saveWhatsNew(this.whatsNewForm.value)
        .then((res) => {
          this.refreshDashboard();
          this.toast.success('Article successfully saved!');
        })
        .catch((e) => {
          console.log(e);
          this.toast.error('There was an error saving the article');
        });
    } else {
      return this.updateWhatsNew(this.articleToEdit.id, this.whatsNewForm.value)
        .then((res) => {
          this.refreshDashboard();
          this.toast.success('Article successfully saved!');
        })
        .catch((e) => {
          console.log(e);
          this.toast.error('There was an error saving the article');
        });
    }
  }

  confirmSaveTutorial() {
    if (!this.editMode) {
      return this.saveHelp(this.tutorialForm.value)
        .then((res) => {
          this.toast.success('Tutorial successfully saved!');
        })
        .then(() => {
          if (this.tutorials.length === 4) {
            this.tutorialToDelete = this.tutorials[0];
            this.confirmDeleteTutorial(true);
          }
          this.refreshDashboard();
        })
        .catch((e) => {
          console.log(e);
          this.toast.error('There was an error saving the tutorial');
        });
    } else {
      return this.updateHelp(this.tutorialToEdit.id, this.tutorialForm.value)
        .then((res) => {
          this.refreshDashboard();
          this.toast.success('Tutorial successfully saved!');
        })
        .catch((e) => {
          console.log(e);
          this.toast.error('There was an error saving the tutorial');
        });
    }
  }

  confirmPublishAppChanges() {
    return this.applicationService.publishFeatures(this.session.selectedAppSysName)
    .then((response) => {
      this.hasFeaturesToPublish = false;
      this.toast.success('Success! Changes were published.');
    })
    .then(() => {
      this.refreshVersionHistory();
    })
    .catch((e) => {
      this.toast.error('Error retrieving updated content: ' + e.json().message);
    });
  }

  confirmDeleteTutorial(hideToast?: boolean) {
    return this.deleteHelp(this.tutorialToDelete.id)
      .then((res) => {
        this.refreshDashboard();
        if (!hideToast) this.toast.success('Tutorial successfully deleted!');
      })
      .catch((e) => {
        console.log(e);
        if (!hideToast) this.toast.error('Error deleting tutorial.');
      });
  }

  updateUserReadStates(articleMap: any) {
    let article = articleMap.article;
    if (!articleMap.read) {
      articleMap.read = true;
      this.user.readArticleIds.push(article.id);

      let remote_cache = {
        read_article_ids: this.user.readArticleIds
      };

      this.userService.updateUserRemoteCache(this.user.id, remote_cache)
      .then(() => {
        this.checkForAnyUnreadArticles(this.articles);
        // we've already updated the in-memory user object so no need to do so here
      })
      .catch((e) => {
        console.log(e);
      });
    }
  }

  getDashboardInformationForApp(sysName: string): Promise<any> {
    return this.session.getDashboardInformation()
      .then(dashboard => {
        if (dashboard.apps[sysName]) return dashboard.apps[sysName];
        return null;
      });
  }

  getColorForStatus(status: string) {
    if (!this.statusOptions[status]) return null;
    return this.statusOptions[status].color;
  }

  getDisplayNameForStatus(status: string) {
    if (!this.statusOptions[status]) return null;
    return this.statusOptions[status].displayName;
  }

  saveWhatsNew(whatsNewObj: any) {
    let body = whatsNewObj;
    return this.http.post(CONFIG.ENDPOINT_URL + 'dashboard/whats_new', body, { headers: this.session.headers() })
      .toPromise()
      .then((res) => {
        return res.json();
      })
      .catch((e) => {
        console.log(e);
        return e.json();
      });
  }

  updateWhatsNew(id: any, whatsNewObj: any) {
    let body = whatsNewObj;
    return this.http.put(CONFIG.ENDPOINT_URL + 'dashboard/whats_new/' + id, body, { headers: this.session.headers() })
      .toPromise()
      .then((res) => {
        return res.json();
      })
      .catch((e) => {
        console.log(e);
        return e.json();
      });
  }

  deleteWhatsNew(id: any) {
    return this.http.delete(CONFIG.ENDPOINT_URL + 'dashboard/whats_new/' + id, { headers: this.session.headers() })
      .toPromise()
      .then((res) => {
        res = res.json();
        this.refreshDashboard();
        this.toast.success('Article successfully deleted!');
      })
      .catch((e) => {
        console.log(e);
        this.toast.error('Error deleting article.');
        return e.json();
      });
  }

  saveHelp(helpObj: any) {
    let body = helpObj;
    return this.http.post(CONFIG.ENDPOINT_URL + 'dashboard/help', body, { headers: this.session.headers() })
      .toPromise()
      .then((res) => {
        return res.json();
      })
      .catch((e) => {
        console.log(e);
        return e.json();
      });
  }

  updateHelp(id: any, helpObj: any) {
    let body = helpObj;
    return this.http.put(CONFIG.ENDPOINT_URL + 'dashboard/help/' + id, body, { headers: this.session.headers() })
      .toPromise()
      .then((res) => {
        return res.json();
      })
      .catch((e) => {
        console.log(e);
        return e.json();
      });
  }

  deleteHelp(id: any) {
    return this.http.delete(CONFIG.ENDPOINT_URL + 'dashboard/help/' + id, { headers: this.session.headers() })
      .toPromise()
      .then((res) => {
        return res.json();
      })
      .catch((e) => {
        console.log(e);
        return e.json();
      });
  }

}



// WEBPACK FOOTER //
// /app/src/app/shared/services/dashboard/dashboard.service.ts